/* eslint-disable id-length */
import { useState } from 'react';
import {
  Popper,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  InputAdornment,
  Divider,
} from '@mui/material';
import PropTypes from 'prop-types';

import { ReactComponent as MagnifyIcon } from '../../Assets/images/svg/dar_magnify.svg';
import ClearIcon from '@mui/icons-material/Clear';
import styles from './checkboxPopper.module.scss';
import ButtonComponent from '../Button/button';

export default function CheckboxPopper({
  anchorEl,
  open,
  onClose,
  options,
  selectedItems,
  onChange,
  enableSearch = false,
  searchInputPlaceholder = 'Search...',
  searchClear = false,
  notFoundText = 'No options found',
  buttonFooterRequired = true,
  onReset,
  onApply,
}) {
  const [search, setSearch] = useState('');

  const checked = selectedItems;

  const handleCheckboxChange = (option) => {
    const newChecked = checked.includes(option)
      ? checked.filter((item) => item !== option)
      : [...checked, option];

    onChange(newChecked);
  };

  const filteredOptions = enableSearch
    ? options.filter((option) =>
      option.toLowerCase().includes(search.toLowerCase()),
    )
    : options;

  const areButtonsDisabled = checked.length === 0;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
      className={styles.popper}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [20, 8],
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box className={styles.container}>
          {enableSearch && (
            <TextField
              variant="standard"
              size="small"
              placeholder={searchInputPlaceholder}
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className={styles['search-input']}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon />
                  </InputAdornment>
                ),
                endAdornment: searchClear && search && (
                  <InputAdornment position="end">
                    <ClearIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSearch('')}
                    />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Box
            display="flex"
            flexDirection="column"
            className={styles['checkbox-container']}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      sx={{
                        '&.Mui-checked': {
                          color: '#6B5CE8 !important',
                        },
                      }}
                      className={styles.checkbox}
                      checked={checked.includes(option)}
                      onChange={() => handleCheckboxChange(option)}
                    />
                  }
                  label={option}
                />
              ))
            ) : (
              <p className={styles['not-found-text']}>{notFoundText}</p>
            )}
          </Box>
          {buttonFooterRequired && (
            <>
              <Divider />
              <Box className={styles['button-container']}>
                <ButtonComponent
                  variant="text"
                  btnCss={styles['reset-btn']}
                  label="Reset"
                  onClick={onReset}
                  disabled={areButtonsDisabled}
                />
                <ButtonComponent
                  variant="contained"
                  btnCss={styles['apply-btn']}
                  label="Apply"
                  onClick={onApply}
                  disabled={areButtonsDisabled}
                />
              </Box>
            </>
          )}
        </Box>
      </ClickAwayListener>
    </Popper>
  );
}

CheckboxPopper.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  enableSearch: PropTypes.bool,
  searchInputPlaceholder: PropTypes.string,
  searchClear: PropTypes.bool,
  notFoundText: PropTypes.string,
  buttonFooterRequired: PropTypes.bool,
  onReset: PropTypes.func,
  onApply: PropTypes.func,
};
